import { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Loader2, Pencil, Check, X } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

/**
 * ItemList — generic CRUD list for goals OR tasks.
 * @param kind "goal" | "task"
 * @param project_slug
 * @param scope "year"|"quarter"|"month"|"day"  (day only valid for kind=task)
 * @param scope_key "2026" / "2026-Q2" / "2026-06" / "2026-06-03"
 * @param title string label
 * @param accent CSS color for accent
 */
export default function ItemList({ kind, project_slug, scope, scope_key, title, accent = "#e6c97a" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const editInputRef = useRef(null);

  const list = kind === "goal" ? adminApi.listGoals : adminApi.listTasks;
  const create = kind === "goal" ? adminApi.createGoal : adminApi.createTask;
  const patch = kind === "goal" ? adminApi.patchGoal : adminApi.patchTask;
  const remove = kind === "goal" ? adminApi.deleteGoal : adminApi.deleteTask;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    list(project_slug, scope, scope_key)
      .then((r) => {
        if (alive) setItems(r.data);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [project_slug, scope, scope_key]);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setAdding(true);
    try {
      const { data } = await create({ project_slug, scope, scope_key, text: text.trim() });
      setItems((p) => [...p, data]);
      setText("");
    } finally {
      setAdding(false);
    }
  };

  const onToggle = async (it) => {
    const optimistic = items.map((x) => (x.id === it.id ? { ...x, done: !x.done } : x));
    setItems(optimistic);
    try {
      await patch(it.id, { done: !it.done });
    } catch {
      setItems(items); // revert
    }
  };

  const onDelete = async (it) => {
    const prev = items;
    setItems(items.filter((x) => x.id !== it.id));
    try {
      await remove(it.id);
    } catch {
      setItems(prev);
    }
  };

  const onEditText = async (it, newText) => {
    if (!newText.trim() || newText === it.text) return;
    const prev = items;
    setItems(items.map((x) => (x.id === it.id ? { ...x, text: newText } : x)));
    try {
      await patch(it.id, { text: newText.trim() });
    } catch {
      setItems(prev);
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setEditText(it.text);
    setConfirmId(null);
    setTimeout(() => editInputRef.current?.focus(), 30);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const commitEdit = async (it) => {
    const next = editText.trim();
    setEditingId(null);
    if (next && next !== it.text) await onEditText(it, next);
  };

  return (
    <div data-testid={`itemlist-${kind}-${scope}-${scope_key}`}>
      <p className="text-xs tracking-widest opacity-60 mb-3" style={{ color: accent }}>
        {title}
      </p>
      <ul className="space-y-2">
        {loading && (
          <li className="text-xs opacity-40 flex items-center gap-2">
            <Loader2 size={12} className="animate-spin" /> loading…
          </li>
        )}
        {!loading && items.length === 0 && (
          <li className="text-xs opacity-30 italic">nothing yet</li>
        )}
        {items.map((it) => (
          <li key={it.id} className="flex items-start gap-3 py-1">
            <button
              onClick={() => onToggle(it)}
              className={`mt-1 w-4 h-4 border flex-shrink-0 transition-colors ${
                it.done
                  ? "border-four-yellow bg-four-yellow"
                  : "border-parchment-faint hover:border-four-yellow"
              }`}
              data-testid={`${kind}-toggle-${it.id}`}
              aria-label="toggle done"
            />
            {editingId === it.id ? (
              <>
                <input
                  ref={editInputRef}
                  type="text"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitEdit(it);
                    if (e.key === "Escape") cancelEdit();
                  }}
                  className="flex-1 bg-transparent border-b border-four-yellow outline-none text-sm"
                  data-testid={`${kind}-edit-input-${it.id}`}
                />
                <button
                  onClick={() => commitEdit(it)}
                  className="text-four-yellow hover:opacity-70"
                  data-testid={`${kind}-edit-save-${it.id}`}
                  aria-label="save edit"
                >
                  <Check size={14} />
                </button>
                <button
                  onClick={cancelEdit}
                  className="opacity-60 hover:opacity-100"
                  data-testid={`${kind}-edit-cancel-${it.id}`}
                  aria-label="cancel edit"
                >
                  <X size={14} />
                </button>
              </>
            ) : (
              <>
                <span
                  className={`flex-1 text-sm cursor-text ${
                    it.done ? "line-through opacity-40" : ""
                  }`}
                  onDoubleClick={() => startEdit(it)}
                  data-testid={`${kind}-text-${it.id}`}
                >
                  {it.text}
                </span>
                <button
                  onClick={() => startEdit(it)}
                  className="opacity-50 hover:opacity-100 hover:text-four-yellow transition-colors"
                  data-testid={`${kind}-edit-${it.id}`}
                  aria-label="edit"
                  title="Edit"
                >
                  <Pencil size={14} />
                </button>
                {confirmId === it.id ? (
                  <>
                    <button
                      onClick={() => {
                        onDelete(it);
                        setConfirmId(null);
                      }}
                      className="text-xs tracking-widest text-red-400 hover:text-red-300"
                      data-testid={`${kind}-delete-confirm-${it.id}`}
                    >
                      DELETE?
                    </button>
                    <button
                      onClick={() => setConfirmId(null)}
                      className="opacity-60 hover:opacity-100"
                      aria-label="cancel delete"
                    >
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setConfirmId(it.id)}
                    className="opacity-50 hover:opacity-100 hover:text-red-400 transition-colors"
                    data-testid={`${kind}-delete-${it.id}`}
                    aria-label="delete"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
      <form onSubmit={onAdd} className="mt-3 flex items-center gap-2">
        <Plus size={14} className="opacity-40" />
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={kind === "goal" ? "add a goal…" : "add a task…"}
          disabled={adding}
          className="flex-1 bg-transparent border-b border-parchment-faint focus:border-four-yellow outline-none text-sm py-1 transition-colors placeholder:opacity-30"
          data-testid={`${kind}-add-input-${scope}-${scope_key}`}
        />
      </form>
    </div>
  );
}
